import TitleStripe from "../../common/TitleStripe";
import NoOcurrence from "../../common/query/NoOcurrence";
import PendenciasFinanceiras from "../../common/query/PendenciasFinanceiras";
import Protestos from "../../common/query/Protestos";
import ChequesSemFundos from "../../common/query/ChequesSemFundos";
import ParticipacaoEmEmpresas from "../../common/query/ParticipacaoEmEmpresas";
import {
  countDadosCadastrais,
  countScore,
  formatDocument,
  getColorScore,
  getImageURLScoreSerasa,
} from "../../../utils/utils";
import QuadroSocietario from "../../common/query/QuadroSocietario";
import ModalReceitaFederal from "../../modals/ModalReceitaFederal";
import { FaRegChartBar, FaUserCheck } from "react-icons/fa";
import { IoReceiptOutline } from "react-icons/io5";
import { TfiBarChart } from "react-icons/tfi";
import { LuBuilding2 } from "react-icons/lu";
import { BsBank } from "react-icons/bs";
import {
  GrDocumentExcel,
  GrDocumentText,
  GrDocumentTime,
  GrMoney,
} from "react-icons/gr";
import { CiMoneyCheck1 } from "react-icons/ci";
import { MdOutlineBalance } from "react-icons/md";
import { HiOutlineCurrencyDollar } from "react-icons/hi";
import { FiUserCheck, FiUsers } from "react-icons/fi";
import { BiLineChartDown } from "react-icons/bi";
import ScoreGaugeApex from "../../common/query/Score";
import Sociedade from "../../common/query/Sociedade";
import moment from "moment";
import { EvaluationScorePJ } from "../../common/query/EvaluationScorePJ";
import HistoricoConsultas from "../../common/query/HistoricoConsultas";

const calculateTenure = (value) => {
  if (!value) return null;
  const parsed = moment(value, ["YYYY-MM-DD", "DD/MM/YYYY"]);
  if (!parsed.isValid()) return null;
  const now = moment();
  const years = now.diff(parsed, "years");
  const months = now.diff(parsed.clone().add(years, "years"), "months");
  const parts = [];
  if (years) parts.push(`${years} ano${years === 1 ? "" : "s"}`);
  if (months) parts.push(`${months} mês${months === 1 ? "" : "es"}`);
  if (!parts.length) return "menos de 1 mês";
  return parts.join(" e ");
};

const calculateCompanyAgeWithMoment = (dateStr) => {
  if (!dateStr) return null;

  // tenta vários formatos comuns
  const foundation = moment(
    dateStr,
    ["DD/MM/YYYY", "YYYY-MM-DD", "YYYYMMDD"],
    true
  );
  if (!foundation.isValid()) return null;

  const now = moment();

  const years = now.diff(foundation, "years");
  const afterYears = foundation.clone().add(years, "years");
  const months = now.diff(afterYears, "months");

  if (years < 0) return null;

  const anosLabel = years > 0 ? `${years} ano${years > 1 ? "s" : ""}` : null;
  const mesesLabel =
    months > 0 ? `${months} mês${months > 1 ? "es" : ""}` : null;

  if (anosLabel && mesesLabel)
    return `${anosLabel} e ${mesesLabel} de atividade`;
  if (anosLabel) return `${anosLabel} de atividade`;
  if (mesesLabel) return `${mesesLabel} de atividade`;

  return "menos de 1 mês de atividade";
};

const CrednetQuery = ({ data, dataFull }) => {
  let riskLabel = "Indefinido";
  let riskClasses = {
    chip: "bg-gray-100 text-gray-700",
    ring: "from-slate-50 to-slate-100",
    dotActive: "bg-gray-300",
    dotIdle: "bg-gray-100",
  };

  const companyAgeLabel = calculateCompanyAgeWithMoment(
    data?.dadosCadastrais?.dataFundacao
  );

  const cadastro = data?.dadosCadastrais || {};
  const foundationDate = cadastro.dataFundacao;
  const tempoAbertura = calculateTenure(foundationDate);
  const situacao = cadastro.situacao || "";
  const isAtivo = situacao.toUpperCase() === "ATIVO";
  const birthDateRaw = cadastro.dataNascimento;
  const birthMoment = birthDateRaw
    ? moment(birthDateRaw, ["YYYY-MM-DD", "DD/MM/YYYY"])
    : null;
  const isBirthValid = birthMoment?.isValid();
  const birthLabel = isBirthValid ? birthMoment.format("DD/MM/YYYY") : null;
  const birthYears = isBirthValid ? moment().diff(birthMoment, "years") : null;
  const ageLabel =
    birthYears != null
      ? `${birthYears} ano${birthYears === 1 ? "" : "s"}`
      : null;

  return (
    <div>
      <>
        <TitleStripe>
          <div className="flex items-center gap-2">
            {data?.dadosCadastrais?.cpf ? (
              <FiUserCheck className="text-blue-700" />
            ) : (
              <LuBuilding2 className="text-blue-700" />
            )}

            <span>Dados Cadastrais / Base II</span>
          </div>
        </TitleStripe>

        {cadastro.nome && (
          <div className="mt-4">
            <div className="">
              <div className="flex gap-3 flex-row items-start justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.3em] text-gray-400">
                    Pessoa Física
                  </p>
                  <p className="text-xl font-semibold text-gray-900">
                    {cadastro.nome}
                  </p>
                </div>
                <span
                  className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] ${
                    cadastro.situacao
                      ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                      : "border-gray-200 bg-gray-50 text-gray-500"
                  }`}
                >
                  {cadastro.situacao || "Situação não informada"}
                </span>
              </div>

              <div className="mt-2 grid gap-3 sm:grid-cols-3">
                <div className="rounded-2xl border border-gray-100 bg-slate-50 p-3">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400">
                    Nascimento
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {birthLabel || "—"}
                  </p>
                  {ageLabel && (
                    <p className="text-[11px] text-gray-500 mt-1">{ageLabel}</p>
                  )}
                </div>
                <div className="rounded-2xl border border-gray-100 bg-slate-50 p-3">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400">
                    Nome da mãe
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {cadastro.nomeMae || "—"}
                  </p>
                </div>
                <div className="rounded-2xl border border-gray-100 bg-slate-50 p-3">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400">
                    Documento
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatDocument(cadastro.cpf) || "Sem documento"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        {cadastro.razaoSocial && (
          <>
            <div className="mt-4  text-xs">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                    Razão Social
                  </p>
                  <p className="text-lg font-semibold text-gray-900">
                    {data?.dadosCadastrais?.razaoSocial || "Indisponível"}
                  </p>
                </div>
                <ModalReceitaFederal cnpj={data?.dadosCadastrais?.cnpj} />
              </div>

              <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
                <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                  Fundação: {data?.dadosCadastrais?.dataFundacao || "—"}
                </span>
                {companyAgeLabel && (
                  <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                    {companyAgeLabel}
                  </span>
                )}
              </div>

              <div className="mt-3 grid gap-2 grid-cols-2">
                {[
                  {
                    label: "CNPJ",
                    value: data?.dadosCadastrais?.cnpj
                      ? formatDocument(data?.dadosCadastrais?.cnpj)
                      : "Não disponível",
                    hint: "Documento",
                  },
                  {
                    label: "Situação",
                    value: data?.dadosCadastrais?.situacao || "Não informado",
                  },
                  {
                    label: "Natureza",
                    value:
                      data?.dadosCadastrais?.naturezaJuridica ||
                      "Não informado",
                    hint: "Perfil jurídico",
                  },
                  {
                    label: "Cidade / UF",
                    value: `${
                      data?.dadosCadastrais?.cidade || "Não informado"
                    } / ${data?.dadosCadastrais?.uf || "--"}`,
                    hint: "Localização",
                  },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="rounded-xl border border-gray-200 bg-slate-50 px-3 py-2"
                  >
                    <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                      {item.label}
                    </p>
                    <p className="text-base font-semibold text-gray-900">
                      {item.value}
                    </p>
                    <p className="text-[10px] text-gray-500">{item.hint}</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
        {data?.dadosCadastrais?.cpf && (
          <div>
            <TitleStripe className="mb-5">
              <div className="flex items-center gap-2">
                <LuBuilding2 className="text-blue-700" />
                <span>Participação em empresas / Base II</span>
              </div>
            </TitleStripe>
            {data?.participacaoEmEmpresas?.length > 0 ||
            data?.socios?.length > 0 ? (
              <ParticipacaoEmEmpresas
                empresas={data?.participacaoEmEmpresas || data?.socios}
              />
            ) : (
              <NoOcurrence />
            )}
          </div>
        )}
        {data?.dadosCadastrais?.cnpj && (
          <div>
            <TitleStripe className="mb-5">
              <div className="flex items-center gap-2">
                <FiUsers className="text-blue-700" />
                <span>Quadro Societário / Base II</span>
              </div>
            </TitleStripe>
            {data?.socios?.length > 0 ? (
              <Sociedade socios={data?.socios} />
            ) : (
              <NoOcurrence />
            )}
          </div>
        )}

        {data?.dadosCadastrais?.cnpj &&
          data?.dadosCadastrais?.administradores && (
            <div>
              <TitleStripe className="mb-5">
                <div className="flex items-center gap-2">
                  <FiUsers className="text-blue-700" />
                  <span>Administradores / Base II</span>
                </div>
              </TitleStripe>
              {data?.administradores?.length > 0 ? (
                <Sociedade socios={data?.administradores} />
              ) : (
                <NoOcurrence />
              )}
            </div>
          )}
      </>

      {data?.score?.score ? (
        <div>
          <TitleStripe className="mb-4">
            <div className="flex items-center gap-2">
              <TfiBarChart className="text-blue-700" />{" "}
              <span>Score de risco / Base II</span>
            </div>
          </TitleStripe>
          <EvaluationScorePJ
            data={data}
            score={data?.score?.score}
            probability={+data?.score?.probabilidadeInadimplencia * 100}
            type="CNPJ"
            hasIncome={false}
          />
        </div>
      ) : (
        <div>
          <TitleStripe className="mb-4">
            <div className="flex items-center gap-2">
              <TfiBarChart className="text-blue-700" />{" "}
              <span>Score de risco / Base II</span>
            </div>
          </TitleStripe>
          <div className="mb-4 mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900">
            Não foi possível calcular o score por insuficiência de informações.
          </div>
        </div>
      )}

      <div id="negativacoes-base-2">
        <TitleStripe className="mb-4">
          <div className="flex items-center gap-2">
            <HiOutlineCurrencyDollar className="text-blue-700" />
            <span>Pendências Financeiras / Base II</span>
          </div>
        </TitleStripe>

        {data?.pendenciasFinanceiras?.length > 0 ? (
          <>
            <PendenciasFinanceiras
              quantidadeTotal={
                data?.resumoConsulta?.pendenciasFinanceiras?.quantidadeTotal
              }
              valorTotal={
                data?.resumoConsulta?.pendenciasFinanceiras?.valorTotal
              }
              pendenciasFinanceiras={data?.pendenciasFinanceiras}
            />
          </>
        ) : (
          <NoOcurrence />
        )}

        <div id="protestos-base-2">
          <TitleStripe className="mb-4">
            <div className="flex items-center gap-2">
              <GrDocumentExcel className="text-blue-700" />
              <span>Títulos protestados / Base II</span>
            </div>
          </TitleStripe>
          {data?.protestos?.length > 0 ? (
            <Protestos
              quantidadeTotal={data?.resumoConsulta?.protestos?.quantidadeTotal}
              valorTotal={data?.resumoConsulta?.protestos?.valorTotal}
              protestos={data?.protestos}
              temVencimento={false}
            />
          ) : (
            <NoOcurrence />
          )}
        </div>
      </div>

      <div id="ccf-base-2">
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <CiMoneyCheck1 className="text-blue-700" />
            <span>Cheques sem fundo / Base II</span>
          </div>
        </TitleStripe>

        {data?.chequesSemFundo?.length > 0 ? (
          <>
            <ChequesSemFundos chequesSemFundos={data?.chequesSemFundo} />
          </>
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-4">
          <div className="flex items-center gap-2">
            <GrDocumentTime className="text-blue-700" />
            <span>Histórico de consultas / Base II</span>
          </div>
        </TitleStripe>

        {data?.historicoConsultas?.length > 0 ? (
          <div className="flex flex-col gap-2">
            <div className="flex-1">
              <HistoricoConsultas
                quantidadeTotal={data?.historicoConsultas?.length}
                historicoConsultas={data?.historicoConsultas}
              />
            </div>
          </div>
        ) : (
          <NoOcurrence />
        )}
      </div>
    </div>
  );
};

export default CrednetQuery;
